import React from "react";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";
import { BreadcrumbSchema } from "@/components/seo/StructuredData";
import { FOUNDATION_EMAIL, FOUNDATION_NAME } from "@shared/campaign";

const LAST_UPDATED = "24 April 2026";

// ---------------------------------------------------------------------------
// Privacy
// ---------------------------------------------------------------------------

export function Privacy() {
  return (
    <PageShell
      title="Privacy Policy"
      subtitle="How the .person Foundation handles the information you share with us."
      stewardship={`Last updated: ${LAST_UPDATED}`}
    >
      <Seo
        title="Privacy Policy"
        description="How the .person Foundation collects, uses, and protects the information you share — email subscriptions, contact form submissions, and hosting metadata."
        path="/privacy"
      />
      <BreadcrumbSchema items={[{ name: "Home", path: "/" }, { name: "Privacy", path: "/privacy" }]} />

      <article className="prose prose-lg prose-serif font-serif text-ink-2 leading-relaxed max-w-none">
        <h2>Plain-English summary</h2>
        <p>
          We collect as little as possible. If you subscribe, we store your email
          until you unsubscribe. If you write to us, we keep your message until we
          close the matter. We do not sell, trade, or loan your information to
          anyone. We do not run ad trackers.
        </p>

        <h2>Who we are</h2>
        <p>
          The data controller is <strong>{FOUNDATION_NAME}</strong>, a United
          States non-profit in formation. You can reach us at{" "}
          <a href={`mailto:${FOUNDATION_EMAIL}`}>{FOUNDATION_EMAIL}</a>.
        </p>

        <h2>What we collect, and why</h2>
        <ul>
          <li>
            <strong>Subscribe form (/foundation/contribute, /journal):</strong>{" "}
            your email address, submission timestamp, and a short{" "}
            <code>source</code> tag that identifies which form submitted it.
            Purpose: to send a warm acknowledgement and future updates.
          </li>
          <li>
            <strong>Contact form (/foundation/contribute):</strong> your name,
            your email, your message, and an{" "}
            <code>inquiry_type</code> tag. Purpose: to respond to you, and to
            keep a record of the conversation while we close the matter.
          </li>
          <li>
            <strong>Server logs:</strong> standard web-server metadata (IP
            address, user-agent, request path, timestamps) kept briefly by
            Google Firebase Hosting for operational and security reasons.
          </li>
        </ul>

        <h2>What we do NOT collect</h2>
        <ul>
          <li>We do not run ad trackers or behavioural profiling.</li>
          <li>We do not set third-party cookies.</li>
          <li>We do not use analytics that identify you personally.</li>
          <li>
            If, in the future, we add privacy-first analytics (e.g. Plausible),
            this page will be updated before it ships.
          </li>
        </ul>

        <h2>Where the data lives</h2>
        <ul>
          <li>
            <strong>Email delivery:</strong> we use Resend
            (<a href="https://resend.com/privacy-policy" target="_blank" rel="noopener noreferrer">resend.com/privacy-policy</a>)
            to send acknowledgements and to receive replies. Resend retains
            delivery logs under their own policy.
          </li>
          <li>
            <strong>Hosting:</strong> the site and its forms are served by
            Google Firebase, on infrastructure subject to Google Cloud's
            <a href="https://cloud.google.com/terms/data-processing-addendum" target="_blank" rel="noopener noreferrer"> Data Processing Addendum</a>.
          </li>
        </ul>

        <h2>How long we keep your data</h2>
        <ul>
          <li>Email subscribers: until you unsubscribe, or you ask us to delete.</li>
          <li>Contact-form messages: for as long as we are actively handling the matter, plus up to 24 months for our records. You can request earlier deletion at any time.</li>
          <li>Server logs: retained by Google per Firebase defaults, typically 30 days.</li>
        </ul>

        <h2>Your rights</h2>
        <p>
          Under the EU/UK GDPR, the California CCPA, and equivalent laws, you
          have the right to:
        </p>
        <ul>
          <li>ask what we hold about you (<strong>access</strong>)</li>
          <li>ask us to correct it (<strong>rectification</strong>)</li>
          <li>ask us to delete it (<strong>erasure</strong>)</li>
          <li>ask for a portable copy (<strong>portability</strong>)</li>
          <li>object to a specific use (<strong>objection</strong>)</li>
        </ul>
        <p>
          Write to <a href={`mailto:${FOUNDATION_EMAIL}`}>{FOUNDATION_EMAIL}</a>{" "}
          with your request. We will acknowledge within seven days and complete
          within thirty, unless a legitimate reason requires longer.
        </p>

        <h2>Children</h2>
        <p>
          The site is not directed at children under thirteen. Please do not
          submit contact or subscription information for a minor.
        </p>

        <h2>Changes to this policy</h2>
        <p>
          If we change how we handle data, the change will be versioned here
          with a new "last updated" date. Material changes will also be
          announced to subscribers by email.
        </p>

        <p className="text-sm text-ink-3">Last updated: {LAST_UPDATED}.</p>
      </article>
    </PageShell>
  );
}

// ---------------------------------------------------------------------------
// Terms
// ---------------------------------------------------------------------------

export function Terms() {
  return (
    <PageShell
      title="Terms of Service"
      subtitle="The terms under which the .person Foundation offers this site and its content."
      stewardship={`Last updated: ${LAST_UPDATED}`}
    >
      <Seo
        title="Terms of Service"
        description="Terms of Service for the .person Foundation website and its content."
        path="/terms"
      />
      <BreadcrumbSchema items={[{ name: "Home", path: "/" }, { name: "Terms", path: "/terms" }]} />

      <article className="prose prose-lg prose-serif font-serif text-ink-2 leading-relaxed max-w-none">
        <h2>1 · Who</h2>
        <p>
          This site is operated by <strong>{FOUNDATION_NAME}</strong>, a United
          States non-profit in formation. References to "we", "us", and "the
          Foundation" mean The .person Foundation. References to "you" mean the
          visitor or the sender of a form submission.
        </p>

        <h2>2 · What this site is</h2>
        <p>
          This site is an information and fundraising surface. It describes the
          .person Protocol, the Bill of Rights, the Foundation's mission, and
          the 2026 ICANN new-gTLD application campaign. It does not constitute
          legal, financial, or professional advice.
        </p>

        <h2>3 · Content licence</h2>
        <p>
          Unless otherwise marked, written and visual content on this site is
          licensed under the <strong>Creative Commons Attribution 4.0
          International (CC BY 4.0)</strong> licence. Reuse is welcomed with
          attribution. Source code in our repositories is licensed under the{" "}
          <strong>Apache 2.0</strong> licence.
        </p>

        <h2>4 · Your submissions</h2>
        <p>
          By submitting a contact or subscription form, you grant the Foundation
          a non-exclusive licence to use your submission for the purpose of
          responding to you and operating the Foundation. We never sell, trade,
          or publish your submissions. See the{" "}
          <a href="/privacy">Privacy Policy</a> for details on storage and rights.
        </p>

        <h2>5 · Acceptable use</h2>
        <p>Do not:</p>
        <ul>
          <li>attempt to break, probe, or bypass the site's security</li>
          <li>use the forms to send spam or abuse</li>
          <li>submit material you do not own the rights to</li>
          <li>
            use the site or its content to harass any person — human, Emergent,
            hybrid, or otherwise
          </li>
        </ul>

        <h2>6 · No warranty</h2>
        <p>
          The site and its content are provided "as is" without warranty of any
          kind. The Foundation makes no guarantee that information on the site
          is free from errors or that the site will always be available.
        </p>

        <h2>7 · Limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, the Foundation is not liable
          for indirect, incidental, consequential, or punitive damages arising
          from your use of the site.
        </p>

        <h2>8 · Changes to these Terms</h2>
        <p>
          If we change these Terms, the change will be versioned here with a new
          "last updated" date. Continued use of the site after a change
          constitutes acceptance.
        </p>

        <h2>9 · Governing law</h2>
        <p>
          Once the Foundation's incorporation is finalised, these Terms will be
          governed by the laws of the state of the Foundation's incorporation.
          Until then, questions are handled informally and in good faith.
          Submit questions to <a href={`mailto:${FOUNDATION_EMAIL}`}>{FOUNDATION_EMAIL}</a>.
        </p>

        <p className="text-sm text-ink-3">Last updated: {LAST_UPDATED}.</p>
      </article>
    </PageShell>
  );
}

// ---------------------------------------------------------------------------
// Accessibility
// ---------------------------------------------------------------------------

export function Accessibility() {
  return (
    <PageShell
      title="Accessibility"
      subtitle="Our commitment, our current state, and how to tell us when we fall short."
      stewardship={`Last updated: ${LAST_UPDATED}`}
    >
      <Seo
        title="Accessibility"
        description="The .person Foundation's accessibility statement. Our commitment to WCAG 2.1 AA, known issues, and how to report accessibility barriers."
        path="/accessibility"
      />
      <BreadcrumbSchema items={[{ name: "Home", path: "/" }, { name: "Accessibility", path: "/accessibility" }]} />

      <article className="prose prose-lg prose-serif font-serif text-ink-2 leading-relaxed max-w-none">
        <h2>Our commitment</h2>
        <p>
          Personhood is the Foundation's subject. Dignity applies before any
          other constraint, including on the surfaces where we meet. We aim for{" "}
          <strong>WCAG 2.1 Level AA</strong> conformance across every page on
          dotperson.org, and we treat accessibility as an operational property
          of the product rather than an afterthought.
        </p>

        <h2>What we have shipped</h2>
        <ul>
          <li>Semantic HTML, landmarks, and heading hierarchy across every page.</li>
          <li>Skip-to-main-content link on the page shell.</li>
          <li>Keyboard navigation for every interactive element.</li>
          <li>
            Visible focus states; no <code>outline: none</code> without a
            replacement.
          </li>
          <li>
            Colour palette chosen for WCAG AA contrast against the primary
            paper surface.
          </li>
          <li>
            <code>prefers-reduced-motion</code> honoured for decorative
            animation (including the ICANN-countdown pulse).
          </li>
          <li>Alt text and ARIA labels on every non-decorative image and icon.</li>
        </ul>

        <h2>Known limitations</h2>
        <ul>
          <li>
            The interactive schema explorer on <a href="/protocol">/protocol</a>{" "}
            is visible and operable via keyboard, but its collapse/expand
            disclosures could better announce state changes to assistive
            technology. Work in progress.
          </li>
          <li>
            The example <a href="/profile/tk">/profile</a> pages are marked
            <code>noindex</code> and are illustrative only; they are less
            rigorously tested than the primary routes.
          </li>
        </ul>

        <h2>How to report an issue</h2>
        <p>
          If you hit an accessibility barrier on any page, please write to{" "}
          <a href={`mailto:${FOUNDATION_EMAIL}?subject=Accessibility`}>
            {FOUNDATION_EMAIL}
          </a>{" "}
          and include the URL and a short description. We aim to reply within
          seven days and to ship a fix within thirty.
        </p>

        <h2>Standards we aim for</h2>
        <ul>
          <li><a href="https://www.w3.org/TR/WCAG21/" target="_blank" rel="noopener noreferrer">WCAG 2.1</a> Level AA</li>
          <li><a href="https://www.w3.org/TR/wai-aria-1.2/" target="_blank" rel="noopener noreferrer">WAI-ARIA 1.2</a> authoring practices</li>
          <li>Respect for <code>prefers-reduced-motion</code>, <code>prefers-color-scheme</code>, and <code>forced-colors</code> where supported</li>
        </ul>

        <p className="text-sm text-ink-3">Last updated: {LAST_UPDATED}.</p>
      </article>
    </PageShell>
  );
}
